import { useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { CopyButton, ExternalLinkButton, IconButton, LinkButton } from "../common/buttons";
import BooleanIcon from "../common/BooleanIcon";
import formatDate from "../common/utils";
import { refreshOnboardingCode } from "../../services/installationService";

const CRM_URL_ROOT = "https://app-eu1.hubspot.com/contacts/26501837/record/0-3";

const labelStyle = { width: "7rem" };
const labelClasses = "text-muted text-nowrap py-1";
const dataClasses = "d-inline-flex align-items-center gap-2 text-nowrap";

export default function InstallationDetailHeader({ installation, onInstallationUpdate }) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefreshCode = async () => {
    setIsRefreshing(true);
    try {
      const { data } = await refreshOnboardingCode(installation.uid);
      onInstallationUpdate(data);
      toast.success("Installation code refreshed");
    } catch {
      toast.error("Failed to refresh installation code");
    } finally {
      setIsRefreshing(false);
    }
  };
  const crmUrl = `${CRM_URL_ROOT}/${installation.deal_id}`;
  const isCodeExpired = installation.onboarding_code_expires_at
    ? new Date(installation.onboarding_code_expires_at) < new Date()
    : true;
  const codeExpiryTitle = (() => {
    if (!installation.onboarding_code_expires_at) {
      return "No expiration set";
    }
    const date = formatDate(installation.onboarding_code_expires_at);
    if (isCodeExpired) {
      return `Expired: ${date}`;
    }
    return `Expires: ${date} (${moment(installation.onboarding_code_expires_at).fromNow()})`;
  })();

  return (
    <table className="table table-sm table-borderless mb-0" style={{ fontSize: "0.8rem" }}>
      <tbody>
        <tr>
          <td className={labelClasses} style={labelStyle}>
            Deal ID
          </td>
          <td className="py-1">
            <span className={dataClasses}>
              <span className={installation.deal_id ? "" : "text-muted"}>{installation.deal_id || "No deal ID"}</span>
              {installation.deal_id && (
                <>
                  <CopyButton value={installation.deal_id} />
                  <ExternalLinkButton href={crmUrl} tooltip="View in CRM" />
                </>
              )}
            </span>
          </td>
        </tr>
        <tr>
          <td className={labelClasses} style={labelStyle}>
            UID
          </td>
          <td className="py-1">
            <span className={dataClasses}>
              <span>{installation.uid}</span>
              <CopyButton value={installation.uid} />
            </span>
          </td>
        </tr>
        <tr>
          <td className={labelClasses} style={labelStyle}>
            Installation code
          </td>
          <td className="py-1">
            <span className={dataClasses}>
              <span title={codeExpiryTitle}>
                {installation.onboarding_code} <BooleanIcon value={!isCodeExpired} circle />
              </span>
              <CopyButton value={installation.onboarding_code} />
              <IconButton
                icon={`fas fa-sync-alt${isRefreshing ? " fa-spin" : ""}`}
                tooltip="Regenerate installation code"
                onClick={handleRefreshCode}
                disabled={isRefreshing}
              />
            </span>
          </td>
        </tr>
        <tr>
          <td className={labelClasses} style={labelStyle}>
            Store
          </td>
          <td className="py-1">
            <LinkButton
              href={`/stores?search=${installation.location}`}
              label={installation.store_name || installation.location || "No store"}
              tooltip={installation.location}
              size="sm"
              disabled={!installation.location}
            />
          </td>
        </tr>
        {/* RTI v2 URL hidden during RTI v3 transition
        <tr>
          <td className={labelClasses} style={labelStyle}>
            URL
          </td>
          <td className="py-1">
            <span className={dataClasses}>
              <span>{installation.url}</span>
              <CopyButton value={installation.url} />
              <ExternalLinkButton href={installation.url} tooltip="Open installation URL" />
            </span>
          </td>
        </tr>
        */}
      </tbody>
    </table>
  );
}
